import { useMemo, useState } from 'react'
import { Award, CheckCircle, Bed, Bath, User } from 'lucide-react'
import StarRating from '../shared/StarRating'
import DetailsTab from './tabs/DetailsTab'
import AmenitiesTab from './tabs/AmenitiesTab'
import ContactTab from './tabs/ContactTab'
import LocationTab from './tabs/LocationTab'
import ReviewsTab from './tabs/ReviewsTab'

interface RoomInfoCardProps {
  roomData: {
    room_id: string | null
    title: string
    rating: number
    is_featured: boolean
    availability: { available: boolean }
    bed: number
    bath: number
    max_guests: number
    rent: number
    listing_status_label?: string
    price_hint?: string
    description: string
    summary?: string
    category?: { name: string } | null
    amenities: Array<{ name: string }>
    address?: string | null
    address_area?: string | null
    maps?: {
      google_maps_url?: string
      street_view_url?: string
      plus_code?: string
      maps_embed_url?: string
    }
    vendor?: {
      name: string
    } | null
    reviews_count: number
    reviews: Array<{
      id: number | string
      user_name: string
      rating: number
      comment: string
      source?: string | null
      created_at?: string | null
    }>
  }
}

const RoomInfoCard = ({ roomData }: RoomInfoCardProps) => {
  const hasAmenities = (roomData.amenities?.length ?? 0) > 0
  const hasReviews = (roomData.reviews?.length ?? 0) > 0

  const tabs = useMemo(() => {
    const base = [
      { id: 'details', name: 'Room Details' },
      ...(hasAmenities ? [{ id: 'amenities', name: 'Amenities' }] : []),
      { id: 'location', name: 'Location' },
      { id: 'reviews', name: hasReviews ? `Reviews (${roomData.reviews_count})` : 'Reviews' },
      { id: 'contact', name: 'Check-in' },
    ]
    return base
  }, [hasAmenities, hasReviews, roomData.reviews_count])

  const [activeTab, setActiveTab] = useState('details')

  const renderTabContent = () => {
    switch (activeTab) {
      case 'details':
        return (
          <DetailsTab
            description={roomData.description}
            summary={roomData.summary}
            category={roomData.category}
            isFeatured={roomData.is_featured}
            isAvailable={roomData.availability.available}
            bed={roomData.bed}
            bath={roomData.bath}
            maxGuests={roomData.max_guests}
            rent={roomData.rent}
            listingStatusLabel={roomData.listing_status_label}
            priceHint={roomData.price_hint}
          />
        )
      case 'amenities':
        return <AmenitiesTab amenities={roomData.amenities} />
      case 'location':
        return (
          <LocationTab
            address={roomData.address}
            address_area={roomData.address_area}
            maps={roomData.maps}
          />
        )
      case 'contact':
        return (
          <ContactTab
            address={roomData.address}
            address_area={roomData.address_area}
            vendor={roomData.vendor ? { name: roomData.vendor.name } : undefined}
          />
        )
      case 'reviews':
        return (
          <ReviewsTab
            rating={roomData.rating}
            reviewsCount={roomData.reviews_count}
            reviews={roomData.reviews}
          />
        )
      default:
        return null
    }
  }

  return (
    <div className="room-card-enhanced h-full rounded-xl sm:rounded-2xl shadow-xl p-4 sm:p-6 lg:p-6">
      <div className="mb-4 sm:mb-6">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">{roomData.title}</h2>
        <div className="flex flex-wrap items-center gap-2 sm:gap-4">
          <StarRating rating={roomData.rating} size="md" showValue />
          {roomData.rating > 0 && roomData.reviews_count > 0 && (
            <span className="text-xs sm:text-sm text-gray-500">({roomData.reviews_count} reviews)</span>
          )}
          {roomData.is_featured && (
            <div className="flex items-center gap-1 text-primary-600">
              <Award className="h-3.5 w-3.5 sm:h-4 sm:w-4 animate-pulse-slow" />
              <span className="text-xs sm:text-sm font-medium premium-badge px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-white">
                Featured
              </span>
            </div>
          )}
          {roomData.availability.available && (
            <div className="flex items-center gap-1 text-green-600">
              <CheckCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              <span className="text-xs sm:text-sm font-medium bg-green-100 text-green-700 px-2 sm:px-3 py-0.5 sm:py-1 rounded-full">
                Available
              </span>
            </div>
          )}
        </div>
        {(roomData.price_hint || roomData.listing_status_label) && (
          <p className="mt-2 text-sm text-primary-700 font-medium">
            {roomData.price_hint || roomData.listing_status_label}
          </p>
        )}
      </div>

      <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-6 sm:mb-8">
        <div className="text-center p-2.5 sm:p-4 bg-gray-50 rounded-lg sm:rounded-xl">
          <Bed className="h-5 w-5 sm:h-6 sm:w-6 text-primary-500 mx-auto mb-1 sm:mb-2" />
          <div className="font-semibold text-gray-900 text-xs sm:text-base">{roomData.bed} Beds</div>
        </div>
        <div className="text-center p-2.5 sm:p-4 bg-gray-50 rounded-lg sm:rounded-xl">
          <Bath className="h-5 w-5 sm:h-6 sm:w-6 text-primary-500 mx-auto mb-1 sm:mb-2" />
          <div className="font-semibold text-gray-900 text-xs sm:text-base">{roomData.bath} Bath</div>
        </div>
        <div className="text-center p-2.5 sm:p-4 bg-gray-50 rounded-lg sm:rounded-xl">
          <User className="h-5 w-5 sm:h-6 sm:w-6 text-primary-500 mx-auto mb-1 sm:mb-2" />
          <div className="font-semibold text-gray-900 text-xs sm:text-base truncate">
            {roomData.vendor?.name || 'Host'}
          </div>
        </div>
      </div>

      <div className="border-b border-gray-200 mb-4 sm:mb-6">
        <nav className="flex overflow-x-auto scrollbar-hide -mb-px space-x-4 sm:space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`tab-button whitespace-nowrap py-2 px-1 border-b-2 font-medium text-xs sm:text-sm ${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600 active'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      <div className="min-h-[160px] sm:min-h-[200px]">{renderTabContent()}</div>
    </div>
  )
}

export default RoomInfoCard
